import type { ActionDto } from '@vision360/contracts';
import { lazy } from 'react';
import { z } from 'zod';
import { widgetText } from '../_sdk/format';
import { toDataState, useActions } from '../_sdk/hooks';
import { defineWidget } from '../_sdk/types';

/**
 * One button that runs an API action (PRD VS-042, step 33 minimal actions):
 * poll a source now, acknowledge every open alert, test Telegram. Every run is
 * audited by the API (`action_runs`), whatever its outcome.
 */

export const PropsSchema = z.object({
  /** Overrides the action's French name, on the button and as the title. */
  label: z.string().max(80).optional(),
  actionId: z.string().min(1).max(80),
  /** One value per `ActionParam` of the action; checked by the API on execute. */
  params: z.record(z.string(), z.union([z.string(), z.number()])).default({}),
  /** Ask before running; the editor pre-fills it from the action. */
  confirm: z.boolean().default(false),
});

export type ActionButtonProps = z.output<typeof PropsSchema>;

/** The action's French name by id (`actions.<id>.name`); an unknown id shows the id. */
export const actionName = (id: string) =>
  widgetText('action-button')(`actions.${id}.name`, { defaultValue: id });

const isUuid = (value: unknown) => z.uuid().safeParse(value).success;

export default defineWidget<ActionButtonProps, ActionDto | null>({
  type: 'action-button',
  category: 'action',
  defaultSize: { w: 2, h: 2 },
  minSize: { w: 2, h: 1 },
  propsSchema: PropsSchema,
  defaults: { params: {}, confirm: false },
  // A source parameter feeds the button: its health badge shows on it.
  sourceIds: (p) => Object.values(p.params).filter(isUuid).map(String),
  useData: (p) =>
    toDataState(useActions(), {
      select: (r) => r.actions.find((a) => a.id === p.actionId) ?? null,
      // An action the API no longer offers: the frame's empty state says so.
      isEmpty: (action) => action === null,
    }),
  Render: lazy(() => import('./render')),
  Editor: lazy(() => import('./editor')),
});
